import { disconnectGoogleApi, cancelChangeEmailApi, useMe } from "@/api/services/main/auth";
import { DetailPageLayout } from "@/components/commons/DetailPageLayout";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { AccountBasicInfoSection } from "@/features/user-setting/components/detail/AccountBasicInfoSection";
import { SecuritySettingsSection } from "@/features/user-setting/components/detail/SecuritySettingsSection";
import { UserSettingFooter } from "@/features/user-setting/components/detail/UserSettingFooter";
import { buildCognitoGoogleLoginUrl } from "@/features/auth/utils/buildCognitoGoogleLoginUrl";
import { encodeCognitoAuthState } from "@/features/auth/utils/cognitoAuthState";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { toast } from "@/utils/toast";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { Navigate } from "react-router-dom";

export const UserSettingDetailPage: FC = () => {
  const { data, isLoading } = useMe();
  const queryClient = useQueryClient();
  const me = data?.data;

  const handleCancelEmailChange = async () => {
    try {
      await cancelChangeEmailApi();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.FETCH_ME] });
      toast.success("メールアドレスの変更をキャンセルしました。");
    } catch (error) {
      showError(error);
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await disconnectGoogleApi();
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER.FETCH_ME] });
      toast.success("Googleログインとの接続を解除しました。");
    } catch (error) {
      showError(error);
    }
  };

  // Kick off the Google link flow. Cognito redirects back to AuthCallbackCognito, which calls
  // /link-google with the returned code; `linkGoogle` in the state marks this authenticated path.
  const handleLinkGoogle = () => {
    const state = encodeCognitoAuthState({ linkGoogle: "1" });
    window.location.href = buildCognitoGoogleLoginUrl(state);
  };

  if (isLoading) return <LoadingArea height={400} />;
  if (!me) return <Navigate to={AppRoutes.pageNotFound} replace />;

  return (
    <DetailPageLayout
      title="ユーザー設定"
      description="アカウント基本情報、およびセキュリティ設定を変更できます。"
      contents={[
        {
          title: "アカウント基本情報",
          content: <AccountBasicInfoSection me={me} pendingEmail={me.newEmail} onCancelEmailChange={handleCancelEmailChange} />,
        },
        {
          title: "セキュリティ設定",
          content: (
            <SecuritySettingsSection
              googleConnected={me.isGoogleLinked}
              googleEmail={me.email}
              onDisconnectGoogle={handleDisconnectGoogle}
              onLinkGoogle={handleLinkGoogle}
            />
          ),
        },
      ]}
      footer={<UserSettingFooter />}
    />
  );
};
